import consola from 'consola'
import type { Observable } from 'rxjs'
import { StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity.ts'
import { useCtxStore } from '~/stores/ctx.store'
import type {
  CompanyDashboardSettings,
  FinancialStatementSettings,
  HideEmptyRowsFilter,
  InPerOfReference,
  KpiListItem,
  LevelsFilter,
  ParsedWappSettingsConfig,
  ParsedWappUserSettingsConfig,
  UserFinancialSettings,
  UserPlanningSettings,
} from '~/types/app-settings'
import { default_user_analysis_settings, default_user_dashboard_settings, default_user_financial_settings, default_user_planning_settings } from '~/types/app-settings'
import type { SectionKey } from '~/types/stcs_wapp_ctx_data.entity'

export const useAppSettings = () => {
  const contextStore = useCtxStore()

  /**
   * Filter out user-specific org settings from company settings map.
   * User settings stored with 'user_settings_*' prefix belong at org level, not company level.
   * @param subSectionMap - The sub-section map to filter
   * @returns Filtered map without user_settings_* keys
   */
  const filterOutUserOrgSettings = (subSectionMap: Record<string, unknown>): Record<string, unknown> => {
    return Object.entries(subSectionMap).reduce(
      (acc, [key, value]) => {
        if (!key.startsWith('user_settings_')) {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, unknown>,
    )
  }

  const ensureStoreStructure = (organizationIdentifier: string, companyIdentifier: string): void => {
    if (!contextStore.ref_app_settings_map[organizationIdentifier]) {
      contextStore.getAppSettings(organizationIdentifier)
    }

    if (!contextStore.ref_app_settings_map[organizationIdentifier]) {
      contextStore.ref_app_settings_map[organizationIdentifier] = {
        id: organizationIdentifier,
        version: '1.0',
        meta: {
          ref_id_org: organizationIdentifier,
          obj_updated_iso_utc: new Date().toISOString(),
          obj_created_iso_utc: new Date().toISOString(),
        },
        org: { sub_section_map: {} },
        comp: {},
      }
    }

    if (!contextStore.ref_app_settings_map[organizationIdentifier].comp) {
      contextStore.ref_app_settings_map[organizationIdentifier].comp = {}
    }

    if (!contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier]) {
      contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier] = {
        sub_section_map: {},
        user: {},
      }
    }
  }

  const saveSettings = async <T>(companyIdentifier: string, settings: T, updateFunction: (organizationIdentifier: string, companyIdentifier: string, settings: T) => Observable<unknown>, settingsType: string): Promise<void> => {
    if (!companyIdentifier) {
      throw new Error(`Invalid companyIdentifier for ${settingsType}`)
    }
    if (!settings) {
      throw new Error(`Invalid settings for ${settingsType}`)
    }

    const organizationIdentifier = contextStore.active_org_id

    try {
      await new Promise<void>((resolve) => {
        updateFunction(organizationIdentifier, companyIdentifier, settings).subscribe({
          next: () => resolve(),
          error: (error: unknown) => {
            consola.warn(`${settingsType} backend sync failed, but local changes are preserved:`, error)
            resolve()
          },
        })
      })
    } catch (error) {
      consola.warn(`Error saving ${settingsType}:`, error)
      throw error
    }
  }

  const getStatementKey = (financialStatement: StcsFstmtEnum): keyof UserFinancialSettings => {
    switch (financialStatement) {
      case StcsFstmtEnum.SFSE_PNL:
        return 'pnl'
      case StcsFstmtEnum.SFSE_BS:
        return 'bs'
      case StcsFstmtEnum.SFSE_CF:
        return 'cf'
      default:
        return 'pnl'
    }
  }

  const getCompanyDashboardSettings = (companyIdentifier: string): Promise<CompanyDashboardSettings> => {
    const organizationIdentifier = contextStore.active_org_id
    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const companySettings = contextStore.getAppSettings(organizationIdentifier, companyIdentifier)
    const dashboardSettings = companySettings?.sub_section_map.dashboard

    if (!dashboardSettings) {
      return Promise.resolve({
        top_cards: ['', '', '', ''],
        kpi_list: [] as KpiListItem[],
      })
    }

    return Promise.resolve(dashboardSettings as CompanyDashboardSettings)
  }

  const saveCompanyDashboardSettings = async (companyIdentifier: string, settings: CompanyDashboardSettings): Promise<void> => {
    const organizationIdentifier = contextStore.active_org_id
    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const currentCompanySettings = contextStore.getAppSettings(organizationIdentifier, companyIdentifier) || { sub_section_map: {} }

    // Filter out user-specific org settings (user_settings_*) - they belong at org level, not company level
    const filteredSubSectionMap = filterOutUserOrgSettings(currentCompanySettings.sub_section_map)

    const updatedCompanySettings = {
      sub_section_map: {
        ...filteredSubSectionMap,
        dashboard: settings,
        cmw: new Date().toISOString(),
      },
    }

    const currentLocalMap = contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].sub_section_map
    const filteredLocalMap = filterOutUserOrgSettings(currentLocalMap)

    contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].sub_section_map = {
      ...filteredLocalMap,
      dashboard: settings,
      cmw: new Date().toISOString(),
    }

    return saveSettings(
      companyIdentifier,
      updatedCompanySettings,
      (organizationIdentifier, companyIdentifier, settings) => contextStore.updateCompAppSettings(organizationIdentifier, companyIdentifier, settings as ParsedWappSettingsConfig),
      'company dashboard settings',
    )
  }

  const parseUserFinancialSettings = (companyIdentifier: string): UserFinancialSettings => {
    const organizationIdentifier = contextStore.active_org_id
    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const userSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier)
    const financialsJson = userSettings?.sub_section_map.financials

    if (!financialsJson) {
      return default_user_financial_settings
    }

    try {
      if (typeof (financialsJson as unknown as Record<string, unknown>).pnl === 'object') {
        return financialsJson as unknown as UserFinancialSettings
      }
      return default_user_financial_settings
    } catch {
      return default_user_financial_settings
    }
  }

  const saveUserFinancialSettings = async (companyIdentifier: string, settings: UserFinancialSettings): Promise<void> => {
    const organizationIdentifier = contextStore.active_org_id
    const userIdentifier = contextStore.ref_user.id.extractId()

    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const updatedUserSettings: ParsedWappUserSettingsConfig = {
      sub_section_map: {
        ...(contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[userIdentifier]?.sub_section_map || {}),
        financials: settings,
      },
    }

    contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[userIdentifier] = updatedUserSettings

    return saveSettings(companyIdentifier, updatedUserSettings, (organizationIdentifier, companyIdentifier, settings) => contextStore.updateUserAppSettings(organizationIdentifier, companyIdentifier, settings), 'user financial settings')
  }

  const getFinancialStatementSettings = (companyIdentifier: string, financialStatement: StcsFstmtEnum): FinancialStatementSettings => {
    const statementKey = getStatementKey(financialStatement)
    const userSettings = parseUserFinancialSettings(companyIdentifier)
    const statementSettings = userSettings[statementKey]

    if (typeof statementSettings === 'object' && statementSettings !== null) {
      return statementSettings as FinancialStatementSettings
    }

    const defaultSettings = default_user_financial_settings[statementKey]
    return defaultSettings as FinancialStatementSettings
  }

  const updateFinancialStatementSettings = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, settings: FinancialStatementSettings): Promise<void> => {
    const statementKey = getStatementKey(financialStatement)
    const currentSettings = parseUserFinancialSettings(companyIdentifier)
    const updatedSettings = {
      ...currentSettings,
      [statementKey]: settings,
    }

    return await saveUserFinancialSettings(companyIdentifier, updatedSettings)
  }

  const getFinancialLevelsFilter = (companyIdentifier: string, financialStatement: StcsFstmtEnum, showDefaults = false): LevelsFilter => {
    if (showDefaults) {
      return { level1: true, level2: true, tags: true, accounts: true, subTotals: false }
    }

    const statementSettings = getFinancialStatementSettings(companyIdentifier, financialStatement)
    return statementSettings.levels || { level1: true, level2: true, tags: false, accounts: true, subTotals: false }
  }

  const updateFinancialLevelsFilter = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, levelsFilter: LevelsFilter): Promise<void> => {
    const currentSettings = getFinancialStatementSettings(companyIdentifier, financialStatement)
    const updatedSettings = { ...currentSettings, levels: levelsFilter }
    return await updateFinancialStatementSettings(companyIdentifier, financialStatement, updatedSettings)
  }

  const getFinancialHideEmptyFilter = (companyIdentifier: string, financialStatement: StcsFstmtEnum, showDefaults = false): HideEmptyRowsFilter => {
    if (showDefaults) {
      return { subTotals: false, level1: false, level2: false, tags: false, accounts: false, otherCashFlow: true }
    }

    const statementSettings = getFinancialStatementSettings(companyIdentifier, financialStatement)
    return statementSettings.hide_empty || { subTotals: false, level1: false, level2: true, tags: true, accounts: true, otherCashFlow: true }
  }

  const updateFinancialHideEmptyFilter = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, hideEmptyFilter: HideEmptyRowsFilter): Promise<void> => {
    const currentSettings = getFinancialStatementSettings(companyIdentifier, financialStatement)
    const updatedSettings = { ...currentSettings, hide_empty: hideEmptyFilter }
    return await updateFinancialStatementSettings(companyIdentifier, financialStatement, updatedSettings)
  }

  const getShowGrowthPercent = (companyIdentifier: string, financialStatement: StcsFstmtEnum, showDefaults = false): boolean => {
    if (showDefaults) {
      return true
    }

    return getFinancialStatementSettings(companyIdentifier, financialStatement).show_growth_percent
  }

  const updateShowGrowthPercent = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, showGrowth: boolean): Promise<void> => {
    const currentSettings = getFinancialStatementSettings(companyIdentifier, financialStatement)
    const updatedSettings = { ...currentSettings, show_growth_percent: showGrowth }
    return await updateFinancialStatementSettings(companyIdentifier, financialStatement, updatedSettings)
  }

  const getHideOldYears = (companyIdentifier: string): boolean => {
    const userSettings = parseUserFinancialSettings(companyIdentifier)
    return userSettings.hide_old_years
  }

  const updateHideOldYears = async (companyIdentifier: string, hideOldMonths: boolean): Promise<void> => {
    const currentSettings = parseUserFinancialSettings(companyIdentifier)
    const updatedSettings = { ...currentSettings, hide_old_years: hideOldMonths }
    return await saveUserFinancialSettings(companyIdentifier, updatedSettings)
  }

  const getHideOldMonths = (companyIdentifier: string, showDefaults = false): boolean => {
    if (showDefaults) {
      return false
    }

    const userSettings = parseUserFinancialSettings(companyIdentifier)
    return userSettings.hide_old_months
  }

  const updateHideOldMonths = async (companyIdentifier: string, hideOldMonths: boolean): Promise<void> => {
    const currentSettings = parseUserFinancialSettings(companyIdentifier)
    const updatedSettings = { ...currentSettings, hide_old_months: hideOldMonths }
    return await saveUserFinancialSettings(companyIdentifier, updatedSettings)
  }

  const getPlanningHideOldYears = (companyIdentifier: string): boolean => {
    const userSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    return userSettings.hide_old_years
  }

  const updatePlanningHideOldYears = async (companyIdentifier: string, hideOldYears: boolean): Promise<void> => {
    const currentSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const updatedSettings = { ...currentSettings, hide_old_years: hideOldYears }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const getPlanningShowStatusColumn = (companyIdentifier: string): boolean => {
    const userSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    return userSettings.show_status_column ?? true
  }

  const updatePlanningShowStatusColumn = async (companyIdentifier: string, showStatusColumn: boolean): Promise<void> => {
    const currentSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const updatedSettings = { ...currentSettings, show_status_column: showStatusColumn }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const getPlanningShowGrowthPercent = (companyIdentifier: string): boolean => {
    const userSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    return userSettings.show_growth_percent ?? true
  }

  const updatePlanningShowGrowthPercent = async (companyIdentifier: string, showGrowthPercent: boolean): Promise<void> => {
    const currentSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const updatedSettings = { ...currentSettings, show_growth_percent: showGrowthPercent }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const getCtxRowReference = (companyIdentifier: string, planIdentifier: string, rowIdentifier: string): { reference_row_id: string; calculation_type: 'growth' | 'percentage' } | undefined => {
    const userSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    return userSettings.ref_plans_map?.[planIdentifier]?.selected_ctx_row?.[rowIdentifier]
  }

  const updateCtxRowReference = async (companyIdentifier: string, planIdentifier: string, rowIdentifier: string, referenceRowIdentifier: string, calculationType: 'growth' | 'percentage'): Promise<void> => {
    const currentSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const updatedPlansMap = {
      ...currentSettings.ref_plans_map,
      [planIdentifier]: {
        ...currentSettings.ref_plans_map?.[planIdentifier],
        selected_ctx_row: {
          ...currentSettings.ref_plans_map?.[planIdentifier]?.selected_ctx_row,
          [rowIdentifier]: {
            reference_row_id: referenceRowIdentifier,
            calculation_type: calculationType,
          },
        },
      },
    }
    const updatedSettings = { ...currentSettings, ref_plans_map: updatedPlansMap }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const getPlanningLevelsFilter = (companyIdentifier: string, financialStatement: StcsFstmtEnum, showDefaults = false): LevelsFilter => {
    if (showDefaults) {
      return { level1: true, level2: false, tags: false, accounts: false, subTotals: true }
    }

    const userSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const statementKey = getStatementKey(financialStatement)
    const levelsKey = `${statementKey}_levels` as keyof UserPlanningSettings

    return (userSettings[levelsKey] as LevelsFilter) || { level1: false, level2: true, tags: false, accounts: false, subTotals: false }
  }

  const updatePlanningLevelsFilter = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, levelsFilter: LevelsFilter): Promise<void> => {
    const currentSettings = parseUserSectionSettings(companyIdentifier, 'plan') as UserPlanningSettings
    const statementKey = getStatementKey(financialStatement)
    const levelsKey = `${statementKey}_levels` as keyof UserPlanningSettings

    const updatedSettings = { ...currentSettings, [levelsKey]: levelsFilter }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const updateLastActualsDate = async (companyIdentifier: string, section: SectionKey, actualsDate: string): Promise<void> => {
    const sharedSections: SectionKey[] = ['financials', 'analysis', 'dashboard']

    if (sharedSections.includes(section)) {
      const organizationIdentifier = contextStore.active_org_id
      const currentUserIdentifier = contextStore.ref_user?.id?.extractId()

      if (!currentUserIdentifier) {
        throw new Error('User Identifier not available')
      }

      ensureStoreStructure(organizationIdentifier, companyIdentifier)

      const currentUserSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier) || { sub_section_map: {} }

      const updatedSubSectionMap = { ...currentUserSettings.sub_section_map }

      sharedSections.forEach((sharedSection) => {
        const currentSectionSettings = parseUserSectionSettings(companyIdentifier, sharedSection)
        updatedSubSectionMap[sharedSection] = { ...currentSectionSettings, last_actuals: actualsDate }
      })

      const updatedUserSettings: ParsedWappUserSettingsConfig = {
        sub_section_map: updatedSubSectionMap,
      }

      contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[currentUserIdentifier] = updatedUserSettings

      return saveSettings(
        companyIdentifier,
        updatedUserSettings,
        (organizationIdentifier, companyIdentifier, settings) => contextStore.updateUserAppSettings(organizationIdentifier, companyIdentifier, settings),
        'user shared sections last actuals',
      )
    }

    const currentSettings = parseUserSectionSettings(companyIdentifier, section)
    const updatedSettings = { ...currentSettings, last_actuals: actualsDate }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const getDefaultUserSectionSettings = (section: SectionKey) => {
    switch (section) {
      case 'analysis':
        return default_user_analysis_settings
      case 'dashboard':
        return default_user_dashboard_settings
      case 'plan':
        return default_user_planning_settings
      case 'financials':
        return default_user_financial_settings
      default:
        return default_user_analysis_settings
    }
  }

  const parseUserSectionSettings = (companyIdentifier: string, section: SectionKey) => {
    const organizationIdentifier = contextStore.active_org_id
    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const userSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier)
    const sectionJson = userSettings?.sub_section_map[section]

    if (!sectionJson) {
      return getDefaultUserSectionSettings(section)
    }

    try {
      return typeof sectionJson === 'string' ? JSON.parse(sectionJson) : sectionJson
    } catch {
      return getDefaultUserSectionSettings(section)
    }
  }

  const saveUserSectionSettingsForPlan = async (companyIdentifier: string, section: SectionKey, settings: UserPlanningSettings): Promise<void> => {
    const organizationIdentifier = contextStore.active_org_id
    const currentUserIdentifier = contextStore.ref_user?.id?.extractId()

    if (!currentUserIdentifier) {
      throw new Error('User Identifier not available')
    }

    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const currentUserSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier) || { sub_section_map: {} }
    const updatedUserSettings = {
      sub_section_map: {
        ...currentUserSettings.sub_section_map,
        [section]: settings,
      },
    }

    contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[currentUserIdentifier] = updatedUserSettings

    return saveSettings(companyIdentifier, updatedUserSettings, (organizationIdentifier, companyIdentifier, settings) => contextStore.updateUserAppSettings(organizationIdentifier, companyIdentifier, settings), `user ${section} settings`)
  }

  const getSelectedPlanId = (companyIdentifier: string, section: SectionKey): string | undefined => {
    const sectionSettings = parseUserSectionSettings(companyIdentifier, section)
    return sectionSettings?.selected_plan_id
  }

  const updateSelectedPlanId = async (companyIdentifier: string, section: SectionKey, planIdentifier: string): Promise<void> => {
    const sharedSections: SectionKey[] = ['financials', 'analysis', 'dashboard']

    if (sharedSections.includes(section)) {
      const organizationIdentifier = contextStore.active_org_id
      const currentUserIdentifier = contextStore.ref_user?.id?.extractId()

      if (!currentUserIdentifier) {
        throw new Error('User Identifier not available')
      }

      ensureStoreStructure(organizationIdentifier, companyIdentifier)

      const currentUserSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier) || { sub_section_map: {} }

      const updatedSubSectionMap = { ...currentUserSettings.sub_section_map }

      sharedSections.forEach((sharedSection) => {
        const currentSectionSettings = parseUserSectionSettings(companyIdentifier, sharedSection)
        updatedSubSectionMap[sharedSection] = { ...currentSectionSettings, selected_plan_id: planIdentifier }
      })

      const updatedUserSettings: ParsedWappUserSettingsConfig = {
        sub_section_map: updatedSubSectionMap,
      }

      contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[currentUserIdentifier] = updatedUserSettings

      return saveSettings(
        companyIdentifier,
        updatedUserSettings,
        (organizationIdentifier, companyIdentifier, settings) => contextStore.updateUserAppSettings(organizationIdentifier, companyIdentifier, settings),
        'user shared sections settings',
      )
    }

    const currentSettings = parseUserSectionSettings(companyIdentifier, section)
    const updatedSettings = { ...currentSettings, selected_plan_id: planIdentifier }
    return await saveUserSectionSettingsForPlan(companyIdentifier, 'plan', updatedSettings)
  }

  const removePlanId = async (companyIdentifier: string, planIdentifierToRemove: string): Promise<void> => {
    const organizationIdentifier = contextStore.active_org_id
    const currentUserIdentifier = contextStore.ref_user?.id?.extractId()

    if (!currentUserIdentifier) {
      throw new Error('User Identifier not available')
    }

    ensureStoreStructure(organizationIdentifier, companyIdentifier)

    const currentUserSettings = contextStore.getUserAppSettings(organizationIdentifier, companyIdentifier) || { sub_section_map: {} }

    const allSections: SectionKey[] = ['financials', 'analysis', 'dashboard', 'plan']

    const updatedSubSectionMap = { ...currentUserSettings.sub_section_map }
    let hasChanges = false

    allSections.forEach((section) => {
      const currentSectionSettings = parseUserSectionSettings(companyIdentifier, section)

      if (currentSectionSettings?.selected_plan_id === planIdentifierToRemove) {
        updatedSubSectionMap[section] = {
          ...currentSectionSettings,
          selected_plan_id: undefined,
        }
        hasChanges = true
      }

      if (section === 'plan') {
        const planningSettings = currentSectionSettings as UserPlanningSettings
        if (planningSettings?.ref_plans_map?.[planIdentifierToRemove]) {
          const updatedPlansMap = { ...planningSettings.ref_plans_map }
          delete updatedPlansMap[planIdentifierToRemove]

          updatedSubSectionMap[section] = {
            ...planningSettings,
            ref_plans_map: updatedPlansMap,
          }
          hasChanges = true
        }
      }
    })

    if (hasChanges) {
      const updatedUserSettings: ParsedWappUserSettingsConfig = {
        sub_section_map: updatedSubSectionMap,
      }

      contextStore.ref_app_settings_map[organizationIdentifier].comp[companyIdentifier].user[currentUserIdentifier] = updatedUserSettings

      await saveSettings(
        companyIdentifier,
        updatedUserSettings,
        (organizationIdentifier, companyIdentifier, settings) => contextStore.updateUserAppSettings(organizationIdentifier, companyIdentifier, settings),
        'remove plan ID from all sections and clean up plan-specific settings',
      )
    }
  }

  /**
   * Get the 'in % of' reference for a financial statement comparison view
   * @param companyIdentifier - The company ID
   * @param financialStatement - The financial statement type (PNL, BS, CF)
   * @returns The saved reference or undefined
   */
  const getFinancialInPerOf = (companyIdentifier: string, financialStatement: StcsFstmtEnum): InPerOfReference | undefined => {
    const userSettings = parseUserFinancialSettings(companyIdentifier)
    const key = financialStatement === StcsFstmtEnum.SFSE_PNL ? 'financial_in_per_of_pnl' : financialStatement === StcsFstmtEnum.SFSE_BS ? 'financial_in_per_of_bs' : 'financial_in_per_of_cf'
    return userSettings[key]
  }

  /**
   * Update the 'in % of' reference for a financial statement comparison view
   * @param companyIdentifier - The company ID
   * @param financialStatement - The financial statement type (PNL, BS, CF)
   * @param reference - The reference to save (or undefined to clear)
   */
  const updateFinancialInPerOf = async (companyIdentifier: string, financialStatement: StcsFstmtEnum, reference: InPerOfReference | undefined): Promise<void> => {
    const currentSettings = parseUserFinancialSettings(companyIdentifier)
    const key = financialStatement === StcsFstmtEnum.SFSE_PNL ? 'financial_in_per_of_pnl' : financialStatement === StcsFstmtEnum.SFSE_BS ? 'financial_in_per_of_bs' : 'financial_in_per_of_cf'

    const updatedSettings = {
      ...currentSettings,
      [key]: reference,
    }

    return await saveUserFinancialSettings(companyIdentifier, updatedSettings)
  }

  return {
    getCompanyDashboardSettings,
    saveCompanyDashboardSettings,
    parseUserFinancialSettings,
    saveUserFinancialSettings,
    getFinancialStatementSettings,
    updateFinancialStatementSettings,
    getFinancialLevelsFilter,
    updateFinancialLevelsFilter,
    getFinancialHideEmptyFilter,
    updateFinancialHideEmptyFilter,
    getShowGrowthPercent,
    updateShowGrowthPercent,
    getHideOldYears,
    updateHideOldYears,
    getHideOldMonths,
    updateHideOldMonths,
    getPlanningHideOldYears,
    updatePlanningHideOldYears,
    getPlanningShowStatusColumn,
    updatePlanningShowStatusColumn,
    getPlanningShowGrowthPercent,
    updatePlanningShowGrowthPercent,
    getCtxRowReference,
    updateCtxRowReference,
    getPlanningLevelsFilter,
    updatePlanningLevelsFilter,
    updateLastActualsDate,
    getDefaultUserSectionSettings,
    parseUserSectionSettings,
    saveUserSectionSettingsForPlan,
    getSelectedPlanId,
    updateSelectedPlanId,
    removePlanId,
    getFinancialInPerOf,
    updateFinancialInPerOf,
  }
}
